import React, { useState } from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Grid, TextField, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { get } from "lodash";
import { useFormContext } from "react-hook-form";
import type { Path } from "react-hook-form";
import { Fieldset } from "components/form/Fieldset";
import { FormCheckbox } from "components/form/FormCheckbox";

export type SimpleMachineLimitFormInput = {
  isUnlimited: boolean;
  limit?: number;
  overrun?: number;
};

interface SimpleMachineLimitFormProps<TFieldValues> {
  fieldName: Path<TFieldValues>;
  limitName: string;
  limitItemName: string;
  required?: boolean;
  disabled?: boolean;
}

export function SimpleMachineLimitForm<TFieldValues>(props: SimpleMachineLimitFormProps<TFieldValues>) {
  const { register, formState, watch, trigger, setValue } = useFormContext();
  const { fieldName, limitName, limitItemName, disabled } = props;
  const unlimitedPath = `${fieldName}.isUnlimited`;
  const limitPath = `${fieldName}.limit`;
  const limitErrors = get(formState.errors, limitPath);
  const hasLimitErrors = !!(formState.errors && limitErrors);
  const overrunPath = `${fieldName}.overrun`;
  const overrunErrors = get(formState.errors, overrunPath);
  const hasOverrunErrors = !!(formState.errors && overrunErrors);
  const [localLimitValue, setLocalLimitValue] = useState(watch(limitPath));
  const [localOverrunValue, setLocalOverrunValue] = useState(watch(overrunPath));
  const [useDefaultOverrun, setUseDefaultOverrun] = useState<boolean>(true);

  const setOverrun = () => {
    const value = parseInt(watch(limitPath));

    if (isNaN(value)) {
      setValue(overrunPath, undefined);
      return;
    }

    let overrun: number | undefined = 0;
    if (value >= 50) overrun = 5;
    if (value >= 100) overrun = Math.floor(value / 100) * 10;

    setValue(overrunPath, overrun);
    setLocalOverrunValue(overrun);
    trigger(overrunPath);
  };

  return (
    <Fieldset title={limitName}>
      <Grid
        container
        spacing={0}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid size={4}>
          <FormCheckbox
            inputName={unlimitedPath}
            label="Unlimited?"
            onChange={(value) => {
              setValue(limitPath, value ? undefined : localLimitValue);
              setValue(overrunPath, value ? undefined : localOverrunValue);
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid size={5}>
          <TextField
            fullWidth
            error={hasLimitErrors}
            helperText={limitErrors?.message?.toString()}
            variant="standard"
            label={`or explicit ${limitItemName} limit`}
            disabled={watch(unlimitedPath) === true || disabled}
            {...register(limitPath, {
              valueAsNumber: true,
              shouldUnregister: true,
              validate: {
                greaterThanZeroWhenLimited: (value?: number) => {
                  if (watch(unlimitedPath)) {
                    return true;
                  } else {
                    return (
                      (value !== undefined && value >= 0) ||
                      `The ${limitName.toLowerCase()} must be greater than or equal to 0.`
                    );
                  }
                },
              },
              onChange: () => {
                if (useDefaultOverrun) setOverrun();
              },
            })}
            slotProps={{
              htmlInput: { inputMode: "numeric", pattern: "[0-9]*" },
            }}
          />
        </Grid>
        <Grid size={3}>
          <TextField
            fullWidth
            error={hasOverrunErrors}
            helperText={overrunErrors?.message?.toString()}
            variant="standard"
            label={
              <Typography>
                with overrun
                <Tooltip title="Use default">
                  <RestartAltIcon
                    fontSize="small"
                    sx={{ marginBottom: "-3px", marginLeft: "3px" }}
                    onClick={() => {
                      setUseDefaultOverrun(true);
                      setOverrun();
                    }}
                  />
                </Tooltip>
              </Typography>
            }
            disabled={watch(unlimitedPath) === true || watch(limitPath) === 0 || disabled}
            {...register(overrunPath, {
              shouldUnregister: true,
              setValueAs: (value: string) => {
                if (value === undefined || value === null || value === "") return undefined;
                return parseInt(value);
              },
              validate: {
                greaterOrEqualToZeroWhenLimited: (value?: number) => {
                  if (value === undefined) return true;
                  if (watch(unlimitedPath)) return true;

                  if (isNaN(value)) return "Must be a number";
                  return value >= 0 || "The overrun must be greater or equal to 0.";
                },
              },
              onChange: () => {
                setUseDefaultOverrun(false);
                setLocalLimitValue(isNaN(watch(limitPath)) ? undefined : watch(limitPath));
              },
            })}
            slotProps={{
              htmlInput: { inputMode: "numeric", pattern: "[0-9]*" },
              inputLabel: { shrink: true },
            }}
          />
        </Grid>
      </Grid>
    </Fieldset>
  );
}
